import { z } from 'zod';

/**
 * Enum for valid message types
 */
export const MessageTypeEnum = z.enum([
  'projectConfig',
  'error',
  'code',
  'userMessage',
  'previewUrl',
  'jobDone',
  'jobStarted',
  'general',
  'llmTokensUsed',
  'iconGenerated',
  'appBuild',
  'chatDecisionDialog',
]);

/**
 * Schema for user message attachment in responses
 * Supports both legacy format (base64 content) and new format (public URL from cloud storage)
 */
export const UserMessageAttachmentSchema = z
  .object({
    // Legacy format (backward compatible) - for old messages
    content: z.string().optional().describe('BASE64 encoded content (legacy format)'),

    // New format (cloud storage) - for new messages
    url: z.string().optional().describe('Public URL for frontend display'),

    // Common fields
    mimeType: z.string().describe('MIME type of the attachment'),
    filename: z.string().describe('Original filename'),
  })
  .refine((data) => data.content || data.url, {
    message: 'Either content (legacy) or url (new format) must be provided',
  });

export const ProjectConfigSchema = z.object({
  message: z.string().describe('User friendly message'),
  projectName: z.string().describe('Project name'),
  packageName: z.string().describe('Package name'),
});

export const ErrorTypeEnum = z.enum(['compilation', 'technical', 'fatal', 'rollback']);

export const RollbackDetailsSchema = z.object({
  originalCheckpointId: z.string().describe('Checkpoint ID before rollback attempt'),
  targetCheckpointId: z.string().describe('Checkpoint ID that was being rolled back to'),
  newCheckpointId: z.string().optional().describe('New checkpoint ID created after rollback (if already created)'),
});

export const ErrorSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('compilation'),
    message: z.string().describe('Content of the error message'),
  }),
  z.object({
    type: z.literal('technical'),
    message: z.string().describe('Content of the error message'),
  }),
  z.object({
    type: z.literal('fatal'),
    message: z.string().describe('Content of the error message'),
  }),
  z.object({
    type: z.literal('rollback'),
    message: z.string().describe('Content of the error message'),
    rollbackDetails: RollbackDetailsSchema.describe('Details about the failed rollback operation'),
  }),
]);

// User message schema for responses
export const UserMessageSchema = z.object({
  userId: z.string().describe('ID of the user sending the message'),
  message: z.string().describe('Message content'),
  attachments: z.array(UserMessageAttachmentSchema).optional().describe('List of file attachments'),
});

// Message type for general assistant messages
export const GeneralAssistantMessageTopicEnum = z.enum([
  'buildPlan',
  'followUp',
  'imageDescription',
  'buildingWebPreview',
  'fixingCompilationErrors',
  'changeRequestConfirmation',
  'changeSummary',
  'noGenerationReason',
  'other',
]);
export const GeneralAssistantMessageSchema = z.object({
  topic: GeneralAssistantMessageTopicEnum.describe('Topic of the message'),
  message: z.array(z.string()).describe('Message from the general assistant'),
});

// Terminal message for job processing
export const JobDoneSchema = z.string().describe('ID of the job that was completed');
export const JobStartedSchema = z.string().describe('ID of the job that was started');

// Status enum for code files
export const CodeFileStatusEnum = z.enum(['waiting', 'processing', 'done', 'error']);
export const CodeFileChangeTypeEnum = z.enum(['new', 'modified', 'deleted']);

// Schema for individual file entries
export const CodeFileSchema = z.object({
  purpose: z.string().optional().describe('Purpose or description of the file'),
  status: CodeFileStatusEnum.describe('Current status of the file generation'),
  changeType: CodeFileChangeTypeEnum.describe('Type of change to the file'),
});

// Schema for files map
export const CodeFilesMapSchema = z.record(
  z.string(), // File path as key
  CodeFileSchema
);

// Complete message for code generation status
export const CodeSchema = z
  .object({
    files: CodeFilesMapSchema,
  })
  .describe('Code content with completion status and file metadata');

// Web preview link
export const PreviewUrlSchema = z.string().describe('URL of the preview');

export const GenerationStageEnum = z.enum([
  'chat',
  'code_generation',
  'change_request',
  'code_generation_autofix',
  'change_request_autofix',
  'manual_fix',
]);

export const LlmTokensUsedSchema = z.object({
  modelName: z.string().describe('LLM model name'),
  inputTokens: z.number().describe('Input tokens reported by LLM'),
  outputTokens: z.number().describe('Output tokens reported by LLM'),
  stage: GenerationStageEnum.describe(
    'What stage it happened; Initial generation prompts might be priced differently than later fixes'
  ),
});

export const IconGeneratedSchema = z.object({
  model: z.string().describe('Model used to generate icon'),
  stage: z
    .string()
    .describe('What stage it happened; Initial generation prompts might be priced differently than later fixes'),
});

export const AppBuildSchema = z.object({
  os: z.enum(['android', 'ios']),
});

export const ChatDecisionDialogSchema = z.object({
  title: z.string().describe('Dialog title'),
  message: z.string().describe('Dialog message'),
  options: z.array(
    z.object({
      label: z.string().describe('Option label'),
      description: z.string().describe('Option description'),
      value: z.string().describe('Value associated with this option'),
    })
  ),
});

// Create TypeScript types based on the schemas
export type MessageType = z.infer<typeof MessageTypeEnum>;
export type ProjectConfig = z.infer<typeof ProjectConfigSchema>;
export type ErrorContent = z.infer<typeof ErrorSchema>;
export type UserMessageAttachment = z.infer<typeof UserMessageAttachmentSchema>;
export type UserMessage = z.infer<typeof UserMessageSchema>;
export type CodeFileStatus = z.infer<typeof CodeFileStatusEnum>;
export type CodeFile = z.infer<typeof CodeFileSchema>;
export type CodeFilesMap = z.infer<typeof CodeFilesMapSchema>;
export type CodeContent = z.infer<typeof CodeSchema>;
export type JobDone = z.infer<typeof JobDoneSchema>;
export type JobStarted = z.infer<typeof JobStartedSchema>;
export type PreviewUrl = z.infer<typeof PreviewUrlSchema>;
export type LlmTokensUsed = z.infer<typeof LlmTokensUsedSchema>;
export type IconGenerated = z.infer<typeof IconGeneratedSchema>;
export type AppBuild = z.infer<typeof AppBuildSchema>;
export type GeneralAssistantMessage = z.infer<typeof GeneralAssistantMessageSchema>;
export type GenerationStage = z.infer<typeof GenerationStageEnum>;
export type ChatDecisionDialog = z.infer<typeof ChatDecisionDialogSchema>;

/**
 * Gets the appropriate schema for a given message type
 * @param type The message type
 * @returns The Zod schema for validating content of the specified type
 */
export function getSchemaForType(type: MessageType): z.ZodTypeAny {
  switch (type) {
    case 'projectConfig':
      return ProjectConfigSchema;
    case 'error':
      return ErrorSchema;
    case 'userMessage':
      return UserMessageSchema;
    case 'jobDone':
      return JobDoneSchema;
    case 'jobStarted':
      return JobStartedSchema;
    case 'code':
      return CodeSchema;
    case 'previewUrl':
      return PreviewUrlSchema;
    case 'general':
      return GeneralAssistantMessageSchema;
    case 'llmTokensUsed':
      return LlmTokensUsedSchema;
    case 'iconGenerated':
      return IconGeneratedSchema;
    case 'appBuild':
      return AppBuildSchema;
    case 'chatDecisionDialog':
      return ChatDecisionDialogSchema;
    default:
      throw new Error(`Unknown message type: ${type}`);
  }
}
